import { z } from "zod";
/** Platform-wide identifiers are UUIDv4 strings; robots additionally carry a human serial. */
export const Uuid = z.string().uuid();
/** ISO-8601 UTC timestamp. All timestamps on the wire are UTC; clients localise. */
export const IsoTime = z.string().datetime({ offset: true });
/**
 * Connectivity is tracked independently of what the robot last reported.
 * `stale` means the link is up but no state has arrived within the freshness window.
 */
export const Connectivity = z.enum(["online", "stale", "offline", "never_connected"]);
/**
 * Normalised operating state used by the platform UI. The onboard state machine
 * has its own (richer) states; the agent maps them onto this set and sends the
 * raw value alongside (`rawState`) for diagnostics.
 */
export const OperatingState = z.enum([
    "idle",
    "running",
    "paused",
    "teleop",
    "docked",
    "charging",
    "faulted",
    "estopped",
    "unknown",
]);
/** Error severities used by the centralised error catalog. */
export const Severity = z.enum(["info", "warning", "error", "critical"]);
/** Coarse roles; fine-grained permissions are derived from these (see permissions.ts). */
export const Role = z.enum(["operator", "technician", "admin", "integrator", "fleet_adapter"]);
/** Kinds of ROS interface the onboard gateway can bind. */
export const InterfaceKind = z.enum(["subscribe", "publish", "service", "action"]);
/**
 * Command lifecycle as seen by the platform.
 *
 *   pending ──dispatch──▶ dispatched ──ack──▶ acknowledged ──▶ running ──▶ (terminal)
 *        │                    │  └──reject──▶ rejected
 *        │                    └──link lost──▶ (stays dispatched, visibility=stale)
 *        └──expiresAt passed, never sent──▶ expired
 *
 *   dispatched / acknowledged / running ──outcome not established──▶ unknown
 *   unknown ──robot reports an outcome──▶ that status
 *   unknown ──technician resolves (audited)──▶ succeeded | failed | cancelled
 *
 * `unknown` means the outcome could not be established (the robot forgot the
 * command, never acknowledged it, or reported that it cannot tell). It is NOT
 * terminal: it blocks conflicting work on the robot until the robot reports a
 * real outcome or a technician resolves it, and physical work is never retried
 * automatically. Terminal outcomes for anything that may have reached the robot
 * come from the robot (or an explicit resolution), never from a platform timer.
 */
export const CommandStatus = z.enum([
    "pending",
    "dispatched",
    "acknowledged",
    "rejected",
    "running",
    "succeeded",
    "failed",
    "cancelled",
    "timed_out",
    "expired",
    "unknown",
]);
export const TERMINAL_COMMAND_STATUSES = new Set([
    "rejected",
    "succeeded",
    "failed",
    "cancelled",
    "timed_out",
    "expired",
]);
export function isTerminalCommandStatus(s) {
    return TERMINAL_COMMAND_STATUSES.has(s);
}
/** Outcomes a technician may record for a command whose outcome is `unknown`. */
export const ResolvedOutcome = z.enum(["succeeded", "failed", "cancelled"]);
/** Statuses the robot itself may report for a command. */
export const RobotReportedCommandStatus = z.enum([
    "acknowledged",
    "rejected",
    "running",
    "succeeded",
    "failed",
    "cancelled",
    "timed_out",
    "expired",
    /** The robot cannot establish the outcome (e.g. agent restarted mid-execution). Not terminal. */
    "unknown",
]);
/**
 * Mission lifecycle (owned by Mission Management). Distinct from command status:
 * a mission may span several commands in future (e.g. dock after clean).
 */
export const MissionStatus = z.enum([
    "accepted", // persisted by the platform (API accepted)
    "dispatching", // command created, waiting for robot ack
    "acknowledged", // robot accepted the command
    "running", // robot reports execution started
    "paused",
    "succeeded",
    "failed",
    "cancelled",
    "rejected",
    "expired",
    "unknown", // outcome not established — blocks the robot until reported or resolved
]);
export const TERMINAL_MISSION_STATUSES = new Set([
    "succeeded",
    "failed",
    "cancelled",
    "rejected",
    "expired",
]);
/**
 * Mode of a control lease, and of the authority generation installed on the robot
 * (architecture v0.3 §7.5). `operate` covers autonomous and service work; `teleop`
 * covers manual driving.
 */
export const ControlMode = z.enum(["operate", "teleop"]);
/**
 * What a command does, carried on every `cmd` so the robot can check it against
 * the installed authority mode. Derived by the platform from the command's purpose.
 */
export const CommandMode = z.enum(["mission", "calibration", "recovery", "config", "teleop", "safety", "observe", "other"]);
/**
 * Command modes each installed authority mode admits (robot- and platform-side).
 * `safety` is always admitted; e-stop ENGAGE additionally bypasses the generation
 * check entirely (local safety has precedence over remote authority). `observe` is
 * read-only work that moves nothing and changes no configuration (e.g. exporting the
 * robot's map), so it is admitted under any installation too.
 */
export const MODES_PERMITTED = {
    operate: ["mission", "calibration", "recovery", "config", "other", "safety", "observe"],
    teleop: ["teleop", "safety", "observe"],
};
export function modePermitted(authority, mode) {
    return MODES_PERMITTED[authority].includes(mode);
}
/** Whether the platform currently has a live view of an in-flight execution. */
export const Visibility = z.enum(["live", "stale"]);
/**
 * `home` is the route network's single home node (master_sm idles there; fixtures carry its id).
 * `navigate` is a plain staging waypoint along a corridor. Both match global_path_harness'
 * pose_picker kinds, so poses placed here and there mean the same thing to the robot.
 */
export const WaypointType = z.enum(["dock", "door", "cubicle", "fixture", "home", "navigate"]);
export const CalibrationType = z.enum(["ft_sensor", "tool_changer", "wheels", "camera", "lidar", "all"]);
/**
 * Deployment lifecycle (activation contract, v0.3 §7.8):
 *   queued → transferring (stage command: download, verify, compatibility, stage)
 *          → staged (robot holds the version; activation waits for a safe boundary)
 *          → activating (activate command) → synced (robot acknowledged version + checksum)
 * `failed` from any step; a robot refusing activation because it is busy returns the
 * deployment to `staged` (deferred), not `failed`. `rolled_back`: superseded by a rollback.
 */
export const ConfigDeploymentStatus = z.enum([
    "draft",
    "validating",
    "invalid",
    "queued",
    "transferring",
    "staged",
    "activating",
    "synced",
    "failed",
    "rolled_back",
]);
export const Pose2D = z.object({
    x: z.number().finite(),
    y: z.number().finite(),
    theta: z.number().finite(),
});
//# sourceMappingURL=common.js.map